import React, { useState } from 'react';
import cambioEstadoService from "../../services/cambioEstadoService";
import { obtenerSiguienteEstadoART } from "../../services/art/artEstadoService";
import { toast } from "react-toastify";
import { obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo, subirArchivo } from "../../services/archivoService";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

function GestorEnviadosMedica({ tramite, onAvanzar }) {
    const [cargando, setCargando] = useState(false);
    const [informeMedico, setInformeMedico] = useState("");
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = React.useRef(null);

    React.useEffect(() => {
        cargarArchivos();
    }, []);

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                esDelBackend: true
            }));
            setArchivos(archivosMapeados);
        } catch (error) {
            console.error("Error cargando archivos:", error);
            toast.error("No se pudieron cargar los archivos adjuntos.");
        } finally {
            setCargandoArchivos(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (e) => {
        const archivosSeleccionados = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            archivo: file,
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...archivosSeleccionados]);
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');

        if (archivosPendientes.length === 0) {
            toast.warn('No hay archivos pendientes para subir');
            return;
        }

        setSubiendoArchivos(true);
        try {
            for (const archivo of archivosPendientes) {
                try {
                    await subirArchivo(tramite.idTramite, archivo.archivo);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'subido' } : a)
                    );
                } catch (error) {
                    console.error(`Error subiendo ${archivo.nombre}:`, error);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'error' } : a)
                    );
                }
            }
            toast.success('Archivos procesados');
        } catch (error) {
            console.error('Error al subir archivos:', error);
            toast.error('Error al subir algunos archivos');
        } finally {
            setSubiendoArchivos(false);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        try {
            await descargarArchivo(archivo.idArchivo, archivo.nombre);
        } catch (error) {
            toast.error('Error al descargar archivo');
        }
    };

    const handleEliminarArchivo = async (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (!archivo) return;

        if (archivo.esDelBackend && archivo.idArchivo) {
            try {
                await eliminarArchivo(archivo.idArchivo);
                setArchivos(prev => prev.filter(a => a.id !== id));
                toast.success('Archivo eliminado');
            } catch (error) {
                toast.error('Error al eliminar archivo');
            }
        } else {
            setArchivos(prev => prev.filter(a => a.id !== id));
        }
    };

    const handleConfirmarEnvio = async () => {
        const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

        if (archivosPendientes) {
            toast.error('Debe subir todos los archivos pendientes primero');
            return;
        }

        setCargando(true);
        try {
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, 'Preparando telegrama');
            const comentarioFinal = informeMedico.trim() ?
                `Evaluación de Médica: ${informeMedico}` :
                `Evaluación de Médica completada.`;

            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: siguienteEstado.id,
                comentario: comentarioFinal
            });

            toast.success("Revisión médica confirmada. Avanzando a Preparando Telegrama.");
            onAvanzar();
        } catch (error) {
            console.error(error);
            toast.error("Error al actualizar el estado: " + error.message);
        } finally {
            setCargando(false);
        }
    };

    const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Enviados a la Médica</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <LocalHospitalIcon fontSize="small" className="me-1" />
                    Revisión Médica
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Registre el informe de la médica y opcionalmente adjunte los estudios o respuestas. Luego, avance para preparar el telegrama.</p>
            </div>

            {/* --- SECCIÓN DE FEEDBACK (Informe Médico) --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <DescriptionIcon fontSize="small" className="me-2 mb-1" />
                    Informe de la Médica
                </h6>
                <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Transcriba o resuma la respuesta / informe de la médica aquí..."
                    value={informeMedico}
                    onChange={(e) => setInformeMedico(e.target.value)}
                ></textarea>
                <small className="text-muted mt-1 d-block">Este informe se guardará en el historial del trámite.</small>
            </div>

            {/* --- GESTOR DE ARCHIVOS --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary">Gestión de Archivos (Opcional)</h6>
                {cargandoArchivos ? (
                    <p>Cargando archivos...</p>
                ) : (
                    <>
                        {archivos.length > 0 && (
                            <div className="list-group mb-3">
                                {archivos.map(archivo => (
                                    <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                            <span className="fw-semibold">{archivo.nombre}</span>
                                            <small className="text-muted ms-2">({archivo.tamaño})</small>
                                            <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : archivo.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                {archivo.estado}
                                            </span>
                                        </div>
                                        <div>
                                            {archivo.esDelBackend && (
                                                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleDescargarArchivo(archivo)} title="Descargar">
                                                    <DownloadIcon fontSize="small" />
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(archivo.id)} title="Eliminar">
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                            style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#cfe2ff' }}
                        >
                            <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                            <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                            <p className="mb-0">Arrastra archivos aquí o haz clic para adjuntar el informe firmado o estudios</p>
                        </div>

                        {archivosPendientes && (
                            <div className="d-flex justify-content-center mt-3">
                                <button className="btn btn-primary rounded-pill px-4" onClick={handleSubirArchivos} disabled={subiendoArchivos}>
                                    {subiendoArchivos ? 'Subiendo...' : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- ACCIONES DE SALIDA --- */}
            <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={handleConfirmarEnvio}
                    disabled={cargando || subiendoArchivos || archivosPendientes}
                    title={archivosPendientes ? "Debe subir todos los archivos pendientes primero" : "Avanzar a preparar el telegrama"}
                >
                    <AssignmentTurnedInIcon fontSize="small" />
                    Avanzar a preparar el telegrama
                </button>
            </div>
        </div>
    );
}

export default GestorEnviadosMedica;

